import React, { useState, useRef } from 'react';
import { Table, Form, Button, InputGroup } from 'react-bootstrap';
import { Trash, Calculator } from 'react-bootstrap-icons';
import { toast } from 'react-toastify';
import { FaTrash } from 'react-icons/fa';

import './ItemsTableSection.scss';

interface SelectionGroupItem {
  item_id: number;
  item_name: string;
  price_modifier: number;
  delivery_price_modifier: number;
  online_price_modifier: number;
  desk_price_modifier: number;
  min_selections: number;
  max_selections: number;
}

interface ItemsTableSectionProps {
  items: SelectionGroupItem[];
  onUpdateItem: (itemId: number, field: string, value: number) => void;
  onDeleteItem: (itemId: number) => void;
  onBulkUpdateItem?: (itemId: number, updates: {[field: string]: number}) => void;
}

const ItemsTableSection: React.FC<ItemsTableSectionProps> = ({
  items,
  onUpdateItem,
  onDeleteItem,
  onBulkUpdateItem
}) => {
  const [itemBulkValues, setItemBulkValues] = useState<{[key: number]: number}>({});
  const inputRefs = useRef<{[key: string]: HTMLInputElement | null}>({});

  const handleSetAllPricesForItem = (itemId: number) => {
    const value = itemBulkValues[itemId] || 0;
    
    if (value < 0) {
      toast.error('El precio no puede ser negativo');
      return;
    }
  
    
    // Si existe la función de actualización masiva, úsala
    if (onBulkUpdateItem) {
      const updates = {
        price_modifier: value,
        delivery_price_modifier: value,
        online_price_modifier: value,
        desk_price_modifier: value
      };
      
      onBulkUpdateItem(itemId, updates);
    } else {
      // Fallback: usar las llamadas individuales con setTimeout para evitar conflictos
      const fields = ['price_modifier', 'delivery_price_modifier', 'online_price_modifier', 'desk_price_modifier'];
      
      fields.forEach((field, index) => {
        setTimeout(() => {
          onUpdateItem(itemId, field, value);
        }, index * 100); // 100ms de delay entre cada actualización
      });
    }
    
    // Actualizar los inputs directamente para feedback visual inmediato
    const priceInputs = [
      inputRefs.current[`price_modifier_${itemId}`],
      inputRefs.current[`delivery_price_modifier_${itemId}`],
      inputRefs.current[`online_price_modifier_${itemId}`],
      inputRefs.current[`desk_price_modifier_${itemId}`]
    ];
    
    priceInputs.forEach((input) => {
      if (input) {
        input.value = value.toString();
      }
    });
    
    toast.success(`✅ Todos los precios establecidos en ${value}`);
  };

  const updateItemBulkValue = (itemId: number, value: number) => {
    setItemBulkValues(prev => ({
      ...prev,
      [itemId]: value
    }));
  };

  if (items.length === 0) return null;

  const renderPriceInput = (value: number, itemId: number, field: string, label: string) => (
    <Form.Control
      ref={(ref: HTMLInputElement | null) => {
        inputRefs.current[`${field}_${itemId}`] = ref;
      }}
      type="number"
      min="0"
      step="0.01"
      value={value}
      onChange={(e) => onUpdateItem(itemId, field, Number(e.target.value))}
      className="price-input"
      title={`Precio para ${label}`}
      placeholder="0.00"
    />
  );

  const renderSelectionInput = (value: number, itemId: number, field: string, type: 'min' | 'max') => (
    <div className="selection-input-container">
      <Form.Control
        type="number"
        min="0"
        value={value}
        onChange={(e) => onUpdateItem(itemId, field, Number(e.target.value))}
        className="selection-input"
        title={`Selecciones ${type === 'min' ? 'mínimas' : 'máximas'} para este item`}
      />
      <div className="selection-arrows">
        <button 
          type="button"
          className="arrow-btn arrow-up"
          onClick={() => onUpdateItem(itemId, field, value + 1)}
        >
          ▲
        </button>
        <button 
          type="button"
          className="arrow-btn arrow-down"
          onClick={() => onUpdateItem(itemId, field, Math.max(0, value - 1))}
        >
          ▼
        </button>
      </div>
    </div>
  );

  return (
    <div className="items-table-section">
      {/* Responsive Table */}
      <div className="table-wrapper">
        <Table responsive className="items-configuration-table">
          <thead>
            <tr>
              <th className="item-name-col">
                Item
              </th>
              <th className="bulk-control-col">
                Establecer Precios
              </th>
              <th className="price-col">
                Restaurante
              </th>
              <th className="price-col">
                Delivery
              </th>
              <th className="price-col">
                Online
              </th>
              <th className="price-col">
                Mesa
              </th>
              <th className="selection-col">
                Mín
              </th>
              <th className="selection-col">
                Máx
              </th>
              <th className="actions-col"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              <tr key={item.item_id} className={`item-row ${index % 2 === 0 ? 'even' : 'odd'}`}>
                <td className="item-name-cell">
                  <div className="item-info">
                    <strong className="item-name">{item.item_name}</strong>
                  </div>
                </td>
                <td className="bulk-control-cell">
                  <InputGroup size="sm" className="bulk-price-control">
                    <Form.Control
                      type="number"
                      value={itemBulkValues[item.item_id] || ''}
                      onChange={(e) => updateItemBulkValue(item.item_id, parseFloat(e.target.value) || 0)}
                      placeholder="Precio"
                      step="0.01"
                      className="bulk-price-input"
                    />
                    <Button
                      variant="primary"
                      onClick={() => handleSetAllPricesForItem(item.item_id)}
                      className="bulk-apply-btn"
                      title={`Establecer ${itemBulkValues[item.item_id] || 0} en todos los precios`}
                    >
                      <Calculator size={12} />
                    </Button>
                  </InputGroup>
                </td>
                <td className="price-cell">
                  {renderPriceInput(item.price_modifier, item.item_id, 'price_modifier', 'Restaurante')}
                </td>
                <td className="price-cell">
                  {renderPriceInput(item.delivery_price_modifier, item.item_id, 'delivery_price_modifier', 'Delivery')}
                </td>
                <td className="price-cell">
                  {renderPriceInput(item.online_price_modifier, item.item_id, 'online_price_modifier', 'Online')}
                </td>
                <td className="price-cell">
                  {renderPriceInput(item.desk_price_modifier, item.item_id, 'desk_price_modifier', 'Mesa')}
                </td>
                <td className="selection-cell">
                  {renderSelectionInput(item.min_selections, item.item_id, 'min_selections', 'min')}
                </td>
                <td className="selection-cell">
                  {renderSelectionInput(item.max_selections, item.item_id, 'max_selections', 'max')}
                </td>
                <td className="actions-cell">
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => onDeleteItem(item.item_id)}
                    className="delete-item-btn"
                    type="button"
                    title={`Eliminar ${item.item_name}`}
                  >
                    <FaTrash />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

export default ItemsTableSection;
